import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { AiOutlineFullscreen } from 'react-icons/ai';
import { BsStopCircle } from 'react-icons/bs';
import { FaCamera } from 'react-icons/fa';
import { LuLayoutPanelLeft, LuSquareMousePointer } from 'react-icons/lu';
import { MdCloseFullscreen } from 'react-icons/md';
import { TbBackground, TbScreenShare } from 'react-icons/tb';

import { AnnotationIcon, CalculatorLogo } from '@src/assets/icons';

import { useRoomContext } from '@src/contexts';

import { snapshotRoom, toggleFullscreen } from '@src/helpers';

import { useCalculatorActions, useMagicPenActions, useRecording } from '@src/hooks';

import {
  useRoomStore,
  useMagicPenStore,
  useCalculatorStore,
  useVideoStreamStore,
  useRoomActionsStore,
  useWebinarRoomStore,
  useCtaStore,
  useVirtualBackgroundStore,
} from '@src/stores';
import { useFetchCTAs } from '@src/hooks/queries/useWebinarQuery';

import style from './threeDotsPopper.module.scss';
import { EMeetingType } from '@src/types';

type TMenuItemConfig = {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  className?: string;
};

export const useThreeDotsPopper = () => {
  const [isActive, setIsActive] = useState(false);

  const { currentPeer, canStopStartAnnotating, isPanelist, isCalculatorEnabled } = useRoomContext();

  const { handleHideMagicPen } = useMagicPenActions();

  const { handleStopShareCalculator } = useCalculatorActions();

  const { objects } = useMagicPenStore(
    useShallow((state) => ({
      objects: state.objects,
    }))
  );

  const { setCtas, setIsCtaManagementModalOpen } = useCtaStore(
    useShallow((state) => ({
      setCtas: state.setCtas,
      setIsCtaManagementModalOpen: state.setIsCtaManagementModalOpen,
    }))
  );

  const {
    screenRecordingSettings: { isActive: isScreenRecordingActive },
    hideSelfSettings: { isActive: isHideSelfEnabled, isDisabled: isHideSelfButtonDisabled },
    snapshotSettings: { isActive: isSnapshotEnabled, isDisabled: isSnapshotButtonDisabled },
    fullscreenSettings: { isActive: isFullscreenEnabled, isDisabled: isFullscreenButtonDisabled },
    setHideSelfSettings,
    setSnapshotSettings,
    setFullscreenSettings,
  } = useRoomActionsStore(
    useShallow((state) => ({
      snapshotSettings: state.snapshotSettings,
      hideSelfSettings: state.hideSelfSettings,
      fullscreenSettings: state.fullscreenSettings,
      screenRecordingSettings: state.screenRecordingSettings,
      setHideSelfSettings: state.setHideSelfSettings,
      setFullscreenSettings: state.setFullscreenSettings,
      setSnapshotSettings: state.setSnapshotSettings,
    }))
  );

  const { handleRecordAction, havePermissionToStopRecording, canUserRecord } = useRecording();

  const {
    isViewLayoutOpen,
    isMagicPenVisible,
    isMagicPenAvailable,
    setIsViewLayoutOpen,
    setIsMagicPenVisible,
    meetingType,
  } = useRoomStore(
    useShallow((state) => ({
      isViewLayoutOpen: state.isViewLayoutOpen,
      isMagicPenVisible: state.isMagicPenVisible,
      isMagicPenAvailable: state.isMagicPenAvailable,
      setIsViewLayoutOpen: state.setIsViewLayoutOpen,
      setIsMagicPenVisible: state.setIsMagicPenVisible,
      meetingType: state.meetingType,
    }))
  );

  const { isBackgroundEffectsOpen, setIsBackgroundEffectsOpen } = useVirtualBackgroundStore(
    useShallow((state) => ({
      isBackgroundEffectsOpen: state.isBackgroundEffectsOpen,
      setIsBackgroundEffectsOpen: state.setIsBackgroundEffectsOpen,
    }))
  );

  const {
    isPersonalCalculatorVisible,
    setIsPersonalCalculatorVisible,
    sharedCalculatorData: { isOpen: isSharedCalculatorOpened, owner: sharedCalculatorOwner },
  } = useCalculatorStore(
    useShallow((state) => ({
      isPersonalCalculatorVisible: state.isPersonalCalculatorVisible,
      setIsPersonalCalculatorVisible: state.setIsPersonalCalculatorVisible,
      sharedCalculatorData: state.sharedCalculatorData,
    }))
  );

  const { webinarId, ctaEnabled, calculatorEnabled } = useWebinarRoomStore(
    useShallow((state) => ({
      webinarId: state.webinarId,
      ctaEnabled: state.attendeeDetails?.roomSetting?.ctaSettings?.ctaEnabled ?? true,
      calculatorEnabled: state.attendeeDetails?.roomSetting?.calculatorSettings?.shareCalculatorEnabled ?? false,
    }))
  );

  const shouldShowCalculator = meetingType === EMeetingType.WEBINAR ? calculatorEnabled : isCalculatorEnabled;

  const { refetch: refetchCTAs } = useFetchCTAs(webinarId || undefined, false);

  const handleHideSelfClick = useCallback(
    (active: boolean) => {
      if (!currentPeer) return;
      try {
        useVideoStreamStore.getState().updateVideo(currentPeer.peer_info.peer_id, { isHideMe: active });
      } catch (error) {
        console.error('Failed to toggle hide self view:', error);
      } finally {
        setHideSelfSettings({ isActive: active });
      }
    },
    [currentPeer, setHideSelfSettings]
  );

  const handleFullscreenClick = useCallback(
    async (active: boolean) => {
      try {
        await toggleFullscreen();
      } catch (error) {
        console.error('Error during fullscreen toggle:', error);
      } finally {
        setFullscreenSettings({ isActive: active });
      }
    },
    [setFullscreenSettings]
  );

  const handleSnapshotClick = useCallback(
    async (snapshotActive: boolean) => {
      try {
        if (snapshotActive) {
          await snapshotRoom();
        }
      } catch (error) {
        console.error('Error during snapshot:', error);
      } finally {
        setSnapshotSettings({ isActive: false });
      }
    },
    [setSnapshotSettings]
  );

  const menuItems: TMenuItemConfig[] = useMemo(() => {
    const items: (TMenuItemConfig | false)[] = [
      //CTA
      meetingType === EMeetingType.WEBINAR &&
        ctaEnabled &&
        isPanelist && {
          id: 'ctaButton',
          label: 'CTA Management',
          icon: <LuSquareMousePointer />,
          onClick: async () => {
            setIsActive(false);
            setIsCtaManagementModalOpen(true);

            if (!webinarId) {
              setCtas([]);
              return;
            }

            const result = await refetchCTAs();
            if (result.isError) {
              console.error(result.error);
            }
            setCtas(result.data?.ctas ?? []);
          },
        },

      // Record / Stop recording (optional)
      canUserRecord && {
        id: isScreenRecordingActive ? 'stopRecButton' : 'startRecButton',
        label: havePermissionToStopRecording ? 'Stop recording' : 'Record session',
        icon: <BsStopCircle className={havePermissionToStopRecording ? 'fill-current red' : ''} />,
        onClick: handleRecordAction,
        className: isScreenRecordingActive && !havePermissionToStopRecording ? style.recordingDisabled : '',
      },

      // Snapshot
      {
        id: 'snapshotRoomButton',
        label: 'Snapshot',
        icon: <FaCamera className={isSnapshotEnabled ? 'fill-current red' : ''} />,
        onClick: () => {
          if (!isSnapshotButtonDisabled) {
            handleSnapshotClick(!isSnapshotEnabled);
          }
        },
      },

      // Background effects
      {
        id: 'backgroundEffectsButton',
        label: 'Background effects',
        icon: <TbBackground className="fill-current white" />,
        onClick: () => setIsBackgroundEffectsOpen(!isBackgroundEffectsOpen),
      },

      // Full-screen
      {
        id: 'fullScreenButton',
        label: `${isFullscreenEnabled ? 'Exit' : 'Enter'} full-screen`,
        icon: isFullscreenEnabled ? (
          <MdCloseFullscreen className="fill-current" />
        ) : (
          <AiOutlineFullscreen className="fill-current" />
        ),
        onClick: () => {
          if (!isFullscreenButtonDisabled) {
            handleFullscreenClick(!isFullscreenEnabled);
          }
        },
      },

      // Hide/show self view
      {
        id: 'hideMeButton',
        label: `${isHideSelfEnabled ? 'Show' : 'Hide'} self view`,
        icon: <TbScreenShare className={isHideSelfEnabled ? 'fill-current red' : ''} />,
        onClick: () => {
          if (!isHideSelfButtonDisabled) {
            handleHideSelfClick(!isHideSelfEnabled);
          }
        },
      },

      // View layout
      {
        id: 'viewLayoutButton',
        label: 'View layout',
        icon: <LuLayoutPanelLeft className="fill-current white" />,
        onClick: () => setIsViewLayoutOpen(!isViewLayoutOpen),
      },

      //calculator
      shouldShowCalculator && {
        id: 'calculatorButton',
        label: isPersonalCalculatorVisible ? 'Close Calculator' : 'Open Calculator',
        icon: <CalculatorLogo />,
        onClick: () => {
          if (isSharedCalculatorOpened && currentPeer?.id === sharedCalculatorOwner?.peer_id) {
            handleStopShareCalculator();
          }

          setIsPersonalCalculatorVisible(!isPersonalCalculatorVisible);
        },
      },

      // Magic pen (optional)
      isMagicPenAvailable &&
        canStopStartAnnotating && {
          id: 'magicPenButton',
          label: `${isMagicPenVisible ? 'Stop' : 'Start'} annotating`,
          icon: <AnnotationIcon className="fill-current" />,
          onClick: () =>
            isMagicPenVisible ? handleHideMagicPen(objects.length) : setIsMagicPenVisible(!isMagicPenVisible),
        },
    ];

    return items.filter(Boolean) as TMenuItemConfig[];
  }, [
    meetingType,
    ctaEnabled,
    isPanelist,
    canUserRecord,
    isScreenRecordingActive,
    havePermissionToStopRecording,
    handleRecordAction,
    isSnapshotEnabled,
    isFullscreenEnabled,
    isHideSelfEnabled,
    shouldShowCalculator,
    isPersonalCalculatorVisible,
    isMagicPenAvailable,
    canStopStartAnnotating,
    isMagicPenVisible,
    setIsCtaManagementModalOpen,
    webinarId,
    refetchCTAs,
    setCtas,
    isSnapshotButtonDisabled,
    handleSnapshotClick,
    setIsBackgroundEffectsOpen,
    isBackgroundEffectsOpen,
    isFullscreenButtonDisabled,
    handleFullscreenClick,
    isHideSelfButtonDisabled,
    handleHideSelfClick,
    setIsViewLayoutOpen,
    isViewLayoutOpen,
    isSharedCalculatorOpened,
    currentPeer?.id,
    sharedCalculatorOwner?.peer_id,
    setIsPersonalCalculatorVisible,
    handleStopShareCalculator,
    handleHideMagicPen,
    objects.length,
    setIsMagicPenVisible,
  ]);

  return {
    isActive,
    menuItems,
    setIsActive,
  };
};
